import { Injectable } from "@angular/core";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { FgdsProductType } from "@domain/product/product";
import type { Project } from "@domain/project/base-project";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";

type ItemWithId = { id: string };
type ItemKindTuple = [ItemWithId[], FgdsProductType | MonitoringCategory];

@Injectable({
  providedIn: "root",
})
export class FloorplanItemReferenceMigrationService {
  migrateFloorplanItemReferences(project: Project): void {
    for (const floorplan of project.cachedFloorplans) {
      const itemKindTuples: ItemKindTuple[] = [
        [floorplan.transmitters, FgdsProductType.TRANSMITTER],
        [floorplan.transmitterPlaceholders, FgdsProductType.TRANSMITTER],
        [floorplan.alarmDevices, FgdsProductType.ALARMDEVICE],
        [floorplan.alarmDevicePlaceholders, FgdsProductType.ALARMDEVICE],
        [floorplan.gasWarningCenters, FgdsProductType.GASWARNINGCENTER],
        [floorplan.gasWarningCenterPlaceholders, FgdsProductType.GASWARNINGCENTER],
        [floorplan.signalElements, FgdsProductType.SIGNALELEMENT],
        [floorplan.signalElementPlaceholders, FgdsProductType.SIGNALELEMENT],
        [floorplan.plasticSigns, FgdsProductType.PLASTICSIGN],
        [floorplan.plasticSignPlaceholders, FgdsProductType.PLASTICSIGN],
        [floorplan.patientMonitors, MonitoringCategory.PATIENT_MONITORS],
        [floorplan.patientMonitorPlaceholders, MonitoringCategory.PATIENT_MONITORS],
        [floorplan.centralStations, MonitoringCategory.CENTRAL_STATIONS],
        [floorplan.centralStationPlaceholders, MonitoringCategory.CENTRAL_STATIONS],
        [floorplan.telemetries, MonitoringCategory.TELEMETRY],
        [floorplan.telemetryPlaceholders, MonitoringCategory.TELEMETRY],
        [floorplan.kvms, MonitoringCategory.KVMS],
        [floorplan.kvmPlaceholders, MonitoringCategory.KVMS],
        [floorplan.displays, MonitoringCategory.DISPLAYS],
        [floorplan.displayPlaceholders, MonitoringCategory.DISPLAYS],
        [floorplan.utilities, MonitoringCategory.UTILITY],
        [floorplan.utilityPlaceholders, MonitoringCategory.UTILITY],
      ];

      for (const [items, kind] of itemKindTuples) {
        for (const item of items) {
          project.addProductItemReference(new FloorplanItemReference(floorplan.id, item.id, kind));
        }
      }
    }
  }

  needsMigration(project: Project): boolean {
    for (const floorplan of project.cachedFloorplans) {
      const allItems = [
        ...floorplan.transmitters,
        ...floorplan.transmitterPlaceholders,
        ...floorplan.alarmDevices,
        ...floorplan.alarmDevicePlaceholders,
        ...floorplan.gasWarningCenters,
        ...floorplan.gasWarningCenterPlaceholders,
        ...floorplan.signalElements,
        ...floorplan.signalElementPlaceholders,
        ...floorplan.plasticSigns,
        ...floorplan.plasticSignPlaceholders,
        ...floorplan.patientMonitors,
        ...floorplan.patientMonitorPlaceholders,
        ...floorplan.centralStations,
        ...floorplan.centralStationPlaceholders,
        ...floorplan.telemetries,
        ...floorplan.telemetryPlaceholders,
        ...floorplan.kvms,
        ...floorplan.kvmPlaceholders,
        ...floorplan.displays,
        ...floorplan.displayPlaceholders,
        ...floorplan.utilities,
        ...floorplan.utilityPlaceholders,
      ];

      if (allItems.length === 0) continue;

      const referencedIds = new Set(
        project.productItemReferences
          .filter((r: FloorplanItemReference) => r.floorplanId === floorplan.id)
          .map((r: FloorplanItemReference) => r.floorplanProductItemId),
      );

      const anyUnreferenced = allItems.some((item) => !referencedIds.has(item.id));
      if (anyUnreferenced) return true;
    }
    return false;
  }
}
